<script setup lang="ts">
import type { TDiamondItem, TProductItem } from '~/layers/catalog/data/types'

import DiamondsTableRow from '~/layers/catalog/components/DiamondsTableRow.vue'
import { useCatalogDataProvider } from '~/layers/catalog/composables/catalogDataProvider'
import { useCatalogSort } from '~/layers/catalog/composables/catalogSort'
import { useCatalogStore } from '~/layers/catalog/store/catalog'
import { useCatalogGridStore } from '~/layers/catalog/store/catalogGrid'

const props = defineProps<{
  items: TProductItem[]
  pending: boolean
  pendingMore: boolean
}>()

const diamonds = computed(() =>
  props.items.filter(
    (item): item is TDiamondItem => item.type === 'diamond' && 'shape' in item,
  ),
)
const store = useCatalogStore()
const gridStore = useCatalogGridStore()
const { pending: fetchPending, pendingMore: fetchPendingMore } =
  useCatalogDataProvider()
const catalogSort = useCatalogSort()
const isSingleWord = (value: string): boolean =>
  value.length > 0 && !/\s/.test(value)
const columns = computed(() =>
  [
    { title: 'ID', code: null },
    { title: 'Форма', code: null },
    { title: 'Вес, карат', code: 'weight' },
    { title: 'Цвет', code: 'color' },
    { title: 'Чистота', code: 'clarity' },
    { title: 'Цена', code: 'price' },
    { title: '', code: null },
    { title: '', code: null },
  ].map((column) => ({
    ...column,
    sortable: gridStore.sorting.some((sort) => sort.code === column.code),
  })),
)

function nextDirection(code: string) {
  const direction =
    store.sort.code === code && store.sort.direction === 'asc' ? 'desc' : 'asc'
  return (
    gridStore.sorting.find(
      (sort) => sort.code === code && sort.direction === direction,
    )?.direction ??
    gridStore.sorting.find((sort) => sort.code === code)?.direction
  )
}

function sortColumn(code: string | null) {
  if (!code || fetchPending.value || fetchPendingMore.value) return
  const direction = nextDirection(code)
  if (!direction) return

  catalogSort.set(code, direction)
}
</script>

<template>
  <div
    class="diamonds-table diamonds-table--catalog overflow-x-auto"
    :aria-busy="pending || pendingMore"
  >
    <table class="diamonds-table__table w-full text-left">
      <caption class="sr-only">
        Каталог бриллиантов
      </caption>
      <thead>
        <tr>
          <th
            v-for="(column, index) in columns"
            :key="column.title || index"
            scope="col"
            class="diamonds-table__header-cell"
            :class="{
              'diamonds-table__header-cell--single-line': isSingleWord(
                column.title,
              ),
            }"
            :title="column.title || undefined"
            :aria-sort="
              column.sortable && column.code === store.sort.code
                ? store.sort.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : undefined
            "
          >
            <button
              v-if="column.sortable"
              type="button"
              class="inline-flex items-center gap-1 uppercase disabled:cursor-wait"
              :class="{ 'text-textColor': column.code === store.sort.code }"
              :disabled="pending || pendingMore"
              @click="sortColumn(column.code)"
            >
              {{ column.title }}
              <svg-icon
                name="icon-chevron-down"
                width="20"
                height="20"
                :class="{
                  'rotate-180':
                    column.code === store.sort.code &&
                    store.sort.direction === 'asc',
                }"
              />
            </button>
            <template v-else>{{ column.title }}</template>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="pending">
          <td
            :colspan="columns.length"
            class="diamonds-table__state-cell"
          >
            <span role="status">Загрузка бриллиантов…</span>
          </td>
        </tr>
        <template v-else>
          <DiamondsTableRow
            v-for="item in diamonds"
            :key="item.id"
            :item="item"
          />
        </template>
        <tr v-if="pendingMore">
          <td
            :colspan="columns.length"
            class="diamonds-table__state-cell"
          >
            <span role="status">Загрузка бриллиантов…</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss">
@use '../../../assets/scss/components/diamonds-table' as *;
@use '../../../assets/scss/components/diamonds-table-view' as *;
</style>
